import React from 'react';
import { Stack, Inline } from '@/Components/ui/layout';
import { Heading } from '@/Components/ui/typography';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Checkbox } from '@/Components/ui/checkbox';

function CurrentPosition({ currentPosition, setCurrentPosition }) {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentPosition((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const formValues = {
    title: currentPosition?.title || '',
    employmentType: currentPosition?.employmentType || '',
    companyName: currentPosition?.companyName || '',
    currentRole: currentPosition?.currentRole || false,
    location: currentPosition?.location || '',
    locationType: currentPosition?.locationType || '',
    startMonth: currentPosition?.startMonth || '',
    startYear: currentPosition?.startYear || '',
    endMonth: currentPosition?.endMonth || '',
    endYear: currentPosition?.endYear || '',
  };

  return (
    <section className="flex flex-col items-center -mt-7.5 p-5 bg-transparent text-slate-400 w-full">
      <div className="flex justify-between items-center w-full">
        <Heading level="h2" size="2xl" className="font-bold mb-2.5 ml-3 text-slate-300">
          Current Position
        </Heading>
      </div>

      <form className="w-full max-w-[1000px] flex flex-col gap-5 mt-7.5 -ml-[90px]">
        <Inline spacing="xl" className="justify-between gap-[160px]">
          <Stack spacing="xs" className="flex-1 flex-col h-full">
            <Label htmlFor="title">Title</Label>
            <Input
              type="text"
              id="title"
              name="title"
              className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
              value={formValues.title || ''}
              onChange={handleChange}
              placeholder="Ex: Sales Manager"
            />
          </Stack>
          <Stack spacing="xs" className="flex-1 flex-col h-full">
            <Label htmlFor="employmentType">Employment Type</Label>
            <select
              id="employmentType"
              name="employmentType"
              className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
              value={formValues.employmentType || ''}
              onChange={handleChange}
            >
              <option value="">Select type</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="temporary">Temporary</option>
              <option value="internship">Internship</option>
              <option value="volunteer">Volunteer</option>
            </select>
          </Stack>
        </Inline>

        <Inline spacing="xl" className="justify-between gap-[160px]">
          <Stack spacing="xs" className="flex-1 flex-col h-full">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              type="text"
              id="companyName"
              name="companyName"
              className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
              value={formValues.companyName || ''}
              onChange={handleChange}
              placeholder="Enter company name"
            />
          </Stack>
          <div className="flex gap-2.5 ml-[95px]">
            <Checkbox
              id="currentRole"
              name="currentRole"
              checked={formValues.currentRole || false}
              onCheckedChange={(checked) => handleChange({ target: { name: 'currentRole', type: 'checkbox', checked } })}
              className="mt-7.5"
            />
            <Label htmlFor="currentRole" className="mt-7.5">I am currently working in this role</Label>
          </div>
        </Inline>

        <Inline spacing="xl" className="justify-between gap-[160px]">
          <Stack spacing="xs" className="flex-1 flex-col h-full">
            <Label htmlFor="location">Location</Label>
            <Input
              type="text"
              id="location"
              name="location"
              className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
              value={formValues.location || ''}
              onChange={handleChange}
              placeholder="Enter location"
            />
          </Stack>
          <Stack spacing="xs" className="flex-1 flex-col h-full">
            <Label htmlFor="locationType">Location Type</Label>
            <select
              id="locationType"
              name="locationType"
              className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
              value={formValues.locationType || ''}
              onChange={handleChange}
            >
              <option value="">Select type</option>
              <option value="office">Office</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </Stack>
        </Inline>

        <Inline spacing="xl" className="justify-between gap-[160px]">
          <Stack spacing="xs" className="flex-1 flex-col h-full">
            <Label>Start Date</Label>
            <Inline spacing="sm" className="gap-2.5">
              <select
                name="startMonth"
                className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
                value={formValues.startMonth || ''}
                onChange={handleChange}
              >
                <option value="">Month</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                name="startYear"
                className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
                value={formValues.startYear || ''}
                onChange={handleChange}
              >
                <option value="">Year</option>
                {Array.from({ length: 10 }, (_, i) => (
                  <option key={i} value={2024 - i}>
                    {2024 - i}
                  </option>
                ))}
              </select>
            </Inline>
          </Stack>
          {!formValues.currentRole && (
            <Stack spacing="xs" className="flex-1 flex-col h-full">
              <Label>End Date</Label>
              <Inline spacing="sm" className="gap-2.5">
                <select
                  name="endMonth"
                  className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
                  value={formValues.endMonth || ''}
                  onChange={handleChange}
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <select
                  name="endYear"
                  className="rounded-lg border border-slate-700 bg-transparent text-slate-400 py-2.5 px-2.5 -mt-0.5 transition-all h-[40%] focus:border-green-400 focus:outline-none"
                  value={formValues.endYear || ''}
                  onChange={handleChange}
                >
                  <option value="">Year</option>
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i} value={2024 - i}>
                      {2024 - i}
                    </option>
                  ))}
                </select>
              </Inline>
            </Stack>
          )}
        </Inline>
      </form>
    </section>
  );
}

export default CurrentPosition;
